/*
 * Lulevi: the cart icon and drawer follow the real cart when a page comes back (2026-10-10, reviewer: after «Back» the
 * browser shows the collection page as it was made, before the mug was added, so the icon has no «1» and the drawer says
 * the cart is empty; same with a second tab. People then add the mug again and order two.)
 * When a page is shown again (Back / Forward, from memory or from the browser cache) or a tab comes back into view, the
 * count on the icon is checked against Shopify's cart; only if they differ are the icon and the drawer redrawn (on the
 * cart page itself the page reloads). Nothing changes while they agree, so the cart never flickers.
 */
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const onCartPage = /\/cart\/?$/.test(location.pathname);
  let busy = false, hiddenAt = 0;

  // the number the icon shows now (no bubble = an empty cart; 100+ shows no number)
  const shownCount = () => {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (!bubble) return 0;
    const n = bubble.querySelector('[aria-hidden="true"]');
    return n ? parseInt(n.textContent, 10) || 0 : 100;
  };

  async function redraw() {
    const s = await (await fetch(root + '?sections=cart-icon-bubble,cart-drawer', { cache: 'no-store' })).json();
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
    const icon = document.getElementById('cart-icon-bubble');
    const iconSec = s['cart-icon-bubble'] && parse(s['cart-icon-bubble']).querySelector('.shopify-section');
    if (icon && iconSec) icon.innerHTML = iconSec.innerHTML;
    const drawer = document.querySelector('cart-drawer'), cur = drawer && drawer.querySelector('#CartDrawer');
    const doc = s['cart-drawer'] && parse(s['cart-drawer']);
    const next = doc && doc.querySelector('cart-drawer'), inner = doc && doc.querySelector('#CartDrawer');
    if (cur && next && inner) {
      drawer.classList.toggle('is-empty', next.classList.contains('is-empty'));
      cur.innerHTML = inner.innerHTML;
      const overlay = drawer.querySelector('#CartDrawer-Overlay');
      if (overlay && drawer.close) overlay.addEventListener('click', drawer.close.bind(drawer));
    }
  }

  async function sync() {
    if (busy) return;
    busy = true;
    try {
      const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })).json();
      const count = cart.items.reduce((n, i) => n + (i.properties && i.properties._letters ? 1 : i.quantity), 0); /* letters count once, as on the icon */
      if (Math.min(count, 100) !== shownCount()) {
        if (onCartPage) location.reload();
        else await redraw();
      }
    } catch (e) {
      /* offline or blocked: the page keeps what it shows */
    } finally {
      busy = false;
    }
  }

  // Back / Forward: from the back-forward cache (pageshow.persisted) or rebuilt from the browser's cache
  window.addEventListener('pageshow', (e) => { if (e.persisted) sync(); });
  const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
  if (nav && nav.type === 'back_forward') sync();
  // another tab may have changed the cart while this one was hidden
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') hiddenAt = Date.now();
    else if (hiddenAt && Date.now() - hiddenAt > 1000) sync();
  });
})();

/*
 * Lulevi: the checkout reminder emails show the shopper's own design (2026-10-20, owner: «если у человека есть
 * персонализированный в корзине… чтоб его товар показывало с именем»). Shopify Messaging gives those emails the cart's
 * attributes, never the lines' properties, so the hidden cart attribute _lulevi_designs carries, for each line with a
 * design picture, «picture :: name :: title» (« ;; » between lines, at most 5). Brought in step a moment after every cart
 * change and when a page opens with a full cart; nothing is sent while it already matches (brand/emails/build.py reads it).
 */
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let timer = 0;

  async function syncDesigns() {
    try {
      const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })).json();
      const clean = (s) => String(s || '').replace(/ (;;|::) /g, ' ').trim();
      const value = cart.items
        .filter((i) => i.properties && /^https:\/\/cdn\.shopify\.com\/.+\/uploads\//.test(i.properties._cart_image || ''))
        .slice(0, 5)
        .map((i) => [i.properties._cart_image, i.properties._Name, i.product_title].map(clean).join(' :: '))
        .join(' ;; ');
      if (((cart.attributes && cart.attributes._lulevi_designs) || '') === value) return;
      await fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { _lulevi_designs: value } }),
      });
    } catch (e) {
      /* the reminder then shows the catalogue photos, as before */
    }
  }

  const later = () => { clearTimeout(timer); timer = setTimeout(syncDesigns, 1500); };
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, later);
  if (document.querySelector('#cart-icon-bubble .cart-count-bubble')) later();
})();

/*
 * Lulevi: «Guardar mi cesta para luego» under the checkout button (owner 2026-10-28: «чтоб люди могли сохранять то, что
 * сделали, и корзины… удобно, чтоб работало и полезно»). People design in Instagram's browser at night and leave from the
 * cart; that cart lives only in that browser. The link carries the cart's lines (variant, quantity and the design in the
 * properties), so it opens the same cart in any browser, on any device, days later: sent to oneself, or to whoever pays.
 * A phone sends it with its own share sheet (WhatsApp, mail, notes); elsewhere it is copied. The lines ride in the #hash,
 * which no server or log gets (they hold children's names); theme.liquid takes it off the address into window.luleviCesta
 * before any analytics loads. Opening it adds the lines the cart doesn't have yet and shows the cart.
 */
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const KEY = '#cesta='; // theme.liquid reads the same
  const enc = (o) => btoa(unescape(encodeURIComponent(JSON.stringify(o)))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  const dec = (s) => JSON.parse(decodeURIComponent(escape(atob(s.replace(/-/g, '+').replace(/_/g, '/')))));
  const getCart = async () => (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })).json();

  // the cart is fetched from the touch on: a phone opens its share sheet only right after the tap
  let pending = null;
  document.addEventListener('pointerdown', (e) => { if (e.target.closest('[data-lulevi-save-cart]')) pending = getCart(); }, true);
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-lulevi-save-cart]');
    if (!b) return;
    const cart = await (pending || getCart());
    pending = null;
    const url = location.origin + root + 'cart' + KEY + enc(cart.items.map((i) => [i.variant_id, i.quantity, i.properties || {}]));
    if (navigator.share) {
      try { await navigator.share({ title: b.dataset.title, url }); return; } catch (err) { if (err && err.name === 'AbortError') return; }
    }
    try { await navigator.clipboard.writeText(url); b.textContent = b.dataset.done; } catch (err) { window.prompt(b.dataset.title, url); }
  });

  // the link opened in a tab already on the shop changes only the #hash: load the page, so theme.liquid takes it
  window.addEventListener('hashchange', () => { if (location.hash.startsWith(KEY)) location.reload(); });
  if (!window.luleviCesta) return;
  let lines;
  try { lines = dec(window.luleviCesta); } catch (e) { lines = []; }
  (async () => {
    const have = new Set((await getCart()).items.map((i) => i.variant_id + JSON.stringify(i.properties || {})));
    const items = lines.filter(([id, , p]) => !have.has(id + JSON.stringify(p))).map(([id, quantity, properties]) => ({ id, quantity, properties }));
    const add = (list) => fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: list }) });
    if (items.length && !(await add(items)).ok) for (const it of items) await add([it]); // one gone from the shop must not keep the rest out
    location.replace(root + 'cart');
  })();
})();
